import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Group,
  SegmentedControl,
  Stack,
  Title,
  Button,
  Menu,
  Text,
  Switch,
  Divider,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { VIEW_MODE, TIMELINE_COLORS, MIDNIGHT } from 'constants/menuManagement';
import { breakpoints } from 'styles/theme.variables';
import { generateTempId } from 'helpers/utils';
import {
  hasRolloverHours,
  isDayClosed,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';

import ScheduleSelect from './ScheduleSelect/ScheduleSelect';
import { ChevronDownIconXs } from '@chownow/pantry/icons';

const SCHEDULE_MODE = {
  STANDARD: 'standard',
  HOLIDAY: 'holiday',
};

const VIEW_MODE_LABELS = {
  [VIEW_MODE.TIMELINE]: 'Timeline view',
  [VIEW_MODE.INPUT]: 'Input view',
};

export default function Hours({
  isEditingExisting,
  schedules,
  setSchedules,
  setSpecialHours,
  specialHours,
  deliveryHours,
  setDeliveryHours,
  validationErrors = [],
  deliveryMatchesPickup,
  initialViewMode,
}) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });
  const [scheduleMode, setScheduleMode] = useState(SCHEDULE_MODE.STANDARD);
  // default to true if deliveryMatchesPickup is not set
  const [isSameAsPickup, setIsSameAsPickup] = useState(deliveryMatchesPickup ?? true);
  const [cachedDeliveryHours, setCachedDeliveryHours] = useState(deliveryHours);
  const [viewMode, setViewMode] = useState(initialViewMode || VIEW_MODE.TIMELINE);
  const [hasRollover, setHasRollover] = useState(initialViewMode === VIEW_MODE.INPUT);

  // On mobile, always use input view (timeline is unusable on small screens)
  const effectiveViewMode = isMobile ? VIEW_MODE.INPUT : viewMode;

  const handleScheduleModeChange = newMode => {
    const targetSchedule = newMode === SCHEDULE_MODE.HOLIDAY ? specialHours : schedules;

    // check if target schedule has rollover hours
    const hasRolloverSchedules = targetSchedule?.some(hasRolloverHours);

    // update mode and view at the same time to prevent flash
    setScheduleMode(newMode);
    if (hasRolloverSchedules) {
      setViewMode(VIEW_MODE.INPUT);
      setHasRollover(true);
    } else {
      setHasRollover(false);
    }
  };

  const onRolloverChange = isRollover => {
    setHasRollover(isRollover);
    if (isRollover && viewMode === VIEW_MODE.TIMELINE) {
      setViewMode(VIEW_MODE.INPUT);
    }
  };

  const isSpecial = scheduleMode === SCHEDULE_MODE.HOLIDAY;
  const activeSchedules = isSpecial ? specialHours : schedules;
  const setActiveSchedules = isSpecial ? setSpecialHours : setSchedules;

  // keep delivery hours in sync with pickup hours
  useEffect(() => {
    if (isSameAsPickup) {
      // set delivery hours to current pickup hours with tempIds, don't include uuid
      const deliverySchedules = schedules.map(({ uuid, ...schedule }) => ({
        ...schedule,
        tempId: generateTempId(),
      }));
      setDeliveryHours(deliverySchedules);
    } else {
      // when delivery hours are diff, close delivery hours when that pickup day is closed
      setDeliveryHours(prevDeliveryHours =>
        prevDeliveryHours.map(deliverySchedule => {
          const pickupSchedule = schedules.find(s => s.dayIndex === deliverySchedule.dayIndex);
          if (pickupSchedule && isDayClosed(pickupSchedule)) {
            return { ...deliverySchedule, startTime: MIDNIGHT, endTime: MIDNIGHT };
          }
          return deliverySchedule;
        }),
      );
    }
  }, [schedules, isSameAsPickup]);

  useEffect(() => {
    if (!isSameAsPickup) {
      setCachedDeliveryHours(deliveryHours);
    }
  }, [deliveryHours, isSameAsPickup]);

  const handleSameAsPickupToggle = checked => {
    setIsSameAsPickup(checked);
    if (checked) {
      // save current delivery hours before switching to pickup hours
      setCachedDeliveryHours(deliveryHours);
      // set delivery hours to current pickup hours with tempIds
      const deliverySchedules = schedules.map(schedule => ({
        ...schedule,
        tempId: generateTempId(),
      }));
      setDeliveryHours(deliverySchedules);
    } else {
      // revert to locally saved delivery hours when toggling off
      setDeliveryHours(cachedDeliveryHours);
    }
  };

  // get all validation errors for a specific day and type
  const getValidationErrors = (dayIndex, isDelivery = false) =>
    validationErrors.filter(
      err =>
        (err.dayIndex === dayIndex || String(err.dayIndex).startsWith(`${dayIndex}-`)) &&
        (isDelivery ? err.type === 'delivery' : err.type === 'pickup'),
    );

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={6}>Pickup Hours</Title>
        <Group gap="xs">
          {isEditingExisting && !isMobile && (
            <SegmentedControl
              size="xs"
              value={scheduleMode}
              onChange={handleScheduleModeChange}
              data={[SCHEDULE_MODE.STANDARD, SCHEDULE_MODE.HOLIDAY]}
            />
          )}
          {!isMobile && (
            <Button.Group>
              <Menu position="bottom-end">
                <Menu.Target>
                  <Button
                    size="sm"
                    variant="ghost"
                    rightSection={<ChevronDownIconXs />}
                    disabled={hasRollover}
                  >
                    {VIEW_MODE_LABELS[viewMode]}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => setViewMode(VIEW_MODE.TIMELINE)}>
                    {VIEW_MODE_LABELS[VIEW_MODE.TIMELINE]}
                  </Menu.Item>
                  <Menu.Item onClick={() => setViewMode(VIEW_MODE.INPUT)}>
                    {VIEW_MODE_LABELS[VIEW_MODE.INPUT]}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Button.Group>
          )}
        </Group>
      </Group>

      {isEditingExisting && isMobile && (
        <SegmentedControl
          size="xs"
          w="fit-content"
          value={scheduleMode}
          onChange={handleScheduleModeChange}
          data={[SCHEDULE_MODE.STANDARD, SCHEDULE_MODE.HOLIDAY]}
        />
      )}

      <ScheduleSelect
        schedules={activeSchedules}
        setSchedules={setActiveSchedules}
        isSpecial={isSpecial}
        viewMode={effectiveViewMode}
        timelineColor={isSpecial ? TIMELINE_COLORS.HOLIDAY : TIMELINE_COLORS.PICKUP}
        onRolloverChange={onRolloverChange}
        getValidationErrors={dayIndex => getValidationErrors(dayIndex)}
      />

      {!isSpecial && (
        <>
          <Divider my="sm" />
          <Stack mt="0" gap={isMobile ? 'xs' : undefined}>
            {isMobile ? (
              <Stack gap="xs">
                <Title order={6}>Delivery Hours</Title>
                <Group gap="xs">
                  <Switch
                    checked={isSameAsPickup}
                    onChange={event => handleSameAsPickupToggle(event.currentTarget.checked)}
                  />
                  <Text>Same as pickup hours</Text>
                </Group>
              </Stack>
            ) : (
              <Group justify="space-between">
                <Title order={6}>Delivery Hours</Title>
                <Group gap="xs">
                  <Text>Same as pickup hours</Text>
                  <Switch
                    checked={isSameAsPickup}
                    onChange={event => handleSameAsPickupToggle(event.currentTarget.checked)}
                  />
                </Group>
              </Group>
            )}
            {!isSameAsPickup && (
              <Stack>
                <Text>
                  Hours must be during pickup hours. These hours only apply to ChowNow ordering
                  channels.
                </Text>
                <ScheduleSelect
                  schedules={deliveryHours}
                  setSchedules={setDeliveryHours}
                  isSpecial={false}
                  viewMode={effectiveViewMode}
                  showAddButton={false}
                  timelineColor={TIMELINE_COLORS.DELIVERY}
                  onRolloverChange={onRolloverChange}
                  getValidationErrors={dayIndex => getValidationErrors(dayIndex, true)}
                />
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}

Hours.propTypes = {
  initialViewMode: PropTypes.string,
  isEditingExisting: PropTypes.bool,
  schedules: PropTypes.arrayOf(PropTypes.shape({})),
  setSchedules: PropTypes.func,
  setSpecialHours: PropTypes.func,
  specialHours: PropTypes.arrayOf(PropTypes.shape({})),
  deliveryHours: PropTypes.arrayOf(PropTypes.shape({})),
  setDeliveryHours: PropTypes.func,
  validationErrors: PropTypes.arrayOf(
    PropTypes.shape({
      dayIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      messages: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  deliveryMatchesPickup: PropTypes.bool,
};
